import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard, ImageIcon, FileText, Users, Newspaper, Sparkle, Settings, Inbox,
  UserCog, LogOut, UsersRound, Sparkles, Mail, ShieldCheck, Menu as MenuIcon, LayoutGrid,
  Home as HomeIcon, Layers, MessageSquare, Server, ChevronDown, ChevronRight, X,
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_277e14a4-0f30-4a88-8c2a-65bd1a744cb8/artifacts/o7xpulnt_logo-horizontal-cecit-for-light-bg%20%282%29.png";

// Sidebar grouped into 4 top-level sections so the nav stays scannable as the
// CMS grows. Each entry belongs to exactly one group.
const NAV_GROUPS = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    items: [{ to: "/admin", label: "Panel principal", icon: LayoutDashboard, end: true }],
  },
  {
    key: "home",
    label: "Portada",
    icon: HomeIcon,
    items: [
      { to: "/admin/hero-slides", label: "Hero Slides", icon: ImageIcon },
      { to: "/admin/home-services", label: "Servicios (Home)", icon: LayoutGrid },
      { to: "/admin/homepage-logos", label: "Logos portada", icon: Sparkles },
      { to: "/admin/settings/home", label: "Textos e imágenes", icon: Sparkle },
      { to: "/admin/settings/benefits", label: "Ventajas del Socio", icon: Sparkles },
    ],
  },
  {
    key: "content",
    label: "Contenido",
    icon: Layers,
    items: [
      { to: "/admin/pages", label: "Páginas", icon: FileText },
      { to: "/admin/news", label: "Noticias", icon: Newspaper },
      { to: "/admin/actividades", label: "Actividades", icon: Sparkle },
      { to: "/admin/team", label: "Junta y Equipo", icon: UsersRound },
    ],
  },
  {
    key: "community",
    label: "Comunidad",
    icon: MessageSquare,
    items: [
      { to: "/admin/members", label: "Socios", icon: Users },
      { to: "/admin/submissions", label: "Mensajes", icon: Inbox },
      { to: "/admin/newsletter", label: "Suscriptores", icon: Mail },
      { to: "/admin/settings/newsletter", label: "Configuración newsletter", icon: Settings },
    ],
  },
  {
    key: "system",
    label: "Sistema",
    icon: Server,
    items: [
      { to: "/admin/menus", label: "Menús", icon: MenuIcon },
      { to: "/admin/users", label: "Usuarios", icon: UserCog },
      { to: "/admin/logs", label: "Auditoría", icon: ShieldCheck },
      { to: "/admin/review", label: "Revisión traducciones", icon: Sparkles },
      { to: "/admin/settings/contact", label: "Contacto y datos", icon: Settings },
      { to: "/admin/settings/scripts", label: "Scripts y widgets", icon: Settings },
      { to: "/admin/settings/seo", label: "SEO y hreflang", icon: Settings },
      { to: "/admin/settings/turnstile", label: "Turnstile (anti-bots)", icon: Settings },
      { to: "/admin/settings/analytics", label: "Analítica", icon: Sparkles },
      { to: "/admin/settings/ai", label: "Traducción IA", icon: Sparkle },
    ],
  },
];

function isActivePath(pathname, item) {
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

function groupHasActive(pathname, group) {
  return group.items.some((it) => isActivePath(pathname, it));
}

export default function CmsLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    // Auto-expand the group containing the current route on first render.
    const initial = {};
    for (const g of NAV_GROUPS) initial[g.key] = groupHasActive(location.pathname, g);
    if (!Object.values(initial).some(Boolean)) initial.dashboard = true;
    return initial;
  });

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const Sidebar = (
    <>
      <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <img src={LOGO} alt="CECIT" className="h-9 brightness-0 invert" />
        </Link>
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden p-1 text-white/70 hover:text-white"
          data-testid="cms-drawer-close"
          aria-label="Cerrar menú"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <p className="text-[10px] uppercase tracking-[0.25em] text-cecit-gold px-6 pt-4 pb-2">Backoffice CMS</p>
      <nav className="flex-1 pb-4 overflow-y-auto" data-testid="cms-nav">
        {NAV_GROUPS.map((group) => {
          const GroupIcon = group.icon;
          const isOpen = openGroups[group.key];
          const hasActive = groupHasActive(location.pathname, group);
          const isSingleton = group.items.length === 1;
          if (isSingleton) {
            const it = group.items[0];
            const Icon = it.icon;
            return (
              <NavLink
                key={group.key}
                to={it.to}
                end={it.end}
                data-testid={`cms-nav-${group.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-all ${
                    isActive
                      ? "bg-white/5 text-white border-cecit-gold"
                      : "text-white/60 hover:text-white hover:bg-white/5 border-transparent"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span>{group.label}</span>
              </NavLink>
            );
          }
          return (
            <div key={group.key} className="mb-1">
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                data-testid={`cms-nav-group-${group.key}`}
                className={`w-full flex items-center gap-3 px-6 py-2.5 text-sm border-l-2 transition-all ${
                  hasActive
                    ? "text-white border-cecit-gold"
                    : "text-white/60 hover:text-white hover:bg-white/5 border-transparent"
                }`}
              >
                <GroupIcon className="w-4 h-4" />
                <span className="flex-1 text-left uppercase tracking-widest text-[11px] font-medium">{group.label}</span>
                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
              {isOpen && (
                <div className="mt-0.5">
                  {group.items.map((it) => {
                    const Icon = it.icon;
                    return (
                      <NavLink
                        key={it.to + it.label}
                        to={it.to}
                        end={it.end}
                        data-testid={`cms-nav-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 pl-12 pr-6 py-2 text-[13px] border-l-2 transition-all ${
                            isActive
                              ? "bg-white/5 text-white border-cecit-gold"
                              : "text-white/55 hover:text-white hover:bg-white/5 border-transparent"
                          }`
                        }
                      >
                        <Icon className="w-3.5 h-3.5" />
                        <span>{it.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="p-5 border-t border-white/10">
        <div className="text-xs text-white/60 mb-1 truncate">{user?.name}</div>
        <div className="text-xs text-white/40 mb-3 truncate">{user?.email}</div>
        <button
          onClick={handleLogout}
          data-testid="cms-logout"
          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:text-white hover:bg-white/5 transition-colors border border-white/10"
        >
          <LogOut className="w-3.5 h-3.5" /> Cerrar sesión
        </button>
      </div>
    </>
  );

  return (
    <div className="cms-shell min-h-screen bg-gray-50">
      {/* --- Sidebar desktop (>= lg) --- */}
      <aside
        className="hidden lg:flex w-64 bg-[#0F2040] text-white flex-col fixed inset-y-0 left-0 z-30"
        data-testid="cms-sidebar"
      >
        {Sidebar}
      </aside>

      {/* --- Mobile drawer overlay --- */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setDrawerOpen(false)}
          data-testid="cms-drawer-backdrop"
        />
      )}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 w-72 bg-[#0F2040] text-white flex flex-col z-50 transform transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="cms-drawer"
      >
        {Sidebar}
      </aside>

      {/* --- Main content --- */}
      <div className="lg:ml-64">
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 sticky top-0 z-20">
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-[#0F2040]"
                data-testid="cms-drawer-open"
                aria-label="Abrir menú"
              >
                <MenuIcon className="w-5 h-5" />
              </button>
              <div className="text-xs sm:text-sm text-gray-600 truncate">
                <span className="text-[#C8102E] font-medium">CECIT</span>
                <span className="hidden sm:inline"> · Panel de administración</span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <span className="text-[10px] sm:text-xs uppercase tracking-widest px-2 sm:px-2.5 py-1 bg-[#0F2040] text-cecit-gold">
                {user?.role?.replace("_", " ")}
              </span>
            </div>
          </div>
        </header>
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
